import { useQuery, type QueryClient, type UseQueryResult } from '@tanstack/react-query';
import { useEffect } from 'react';

import { api } from '../api/client';

import { useAuthStore } from './store';
import { type User } from './types';

export const authKeys = {
  me: ['auth', 'me'] as const,
};

/**
 * Imperative fetch for `/auth/me` — used by route `beforeLoad` guards, which
 * run outside the React tree and therefore can't call `useCurrentUser`.
 * Cached against the same query key, so a later `useCurrentUser` call reads
 * from cache rather than re-fetching.
 *
 * Plan 11-01 (BUG-003): syncs the Zustand store on every resolution so
 * non-React readers (Header, DashboardPage) stay in lockstep with the guard's
 * view of auth. Without this, a hard reload populated react-query but left
 * the store at `null`, producing the «Привет, гость!» state with the
 * authenticated app-shell still visible.
 */
export const fetchCurrentUser = (queryClient: QueryClient): Promise<User | null> =>
  queryClient.fetchQuery<User | null>({
    queryKey: authKeys.me,
    queryFn: async () => {
      const result = await api.GET('/api/auth/me');
      let user: User | null;
      if (result.response.status === 401) user = null;
      else if (result.error) throw new Error(result.error.error);
      else user = result.data.user;
      useAuthStore.getState().setCurrentUser(user);
      return user;
    },
    staleTime: Number.POSITIVE_INFINITY,
  });

/**
 * Fetch the currently authenticated user from `/auth/me`.
 * Returns `null` when the server replies 401 (unauthenticated) — the caller
 * can treat "no user" as a normal, non-error state.
 *
 * Keeps the result synced into the Zustand store so non-React code paths
 * (e.g. route `beforeLoad` guards) can read `useAuthStore.getState()`.
 */
export const useCurrentUser = (): UseQueryResult<User | null> => {
  const setCurrentUser = useAuthStore((s) => s.setCurrentUser);

  const query = useQuery<User | null>({
    queryKey: authKeys.me,
    queryFn: async () => {
      const result = await api.GET('/api/auth/me');
      if (result.response.status === 401) return null;
      if (result.error) throw new Error(result.error.error);
      return result.data.user;
    },
    staleTime: Number.POSITIVE_INFINITY,
    retry: false,
  });

  useEffect(() => {
    if (query.data !== undefined) setCurrentUser(query.data);
  }, [query.data, setCurrentUser]);

  return query;
};
